// Copyright 2016 Sorama B.V.

import { autoinject, computedFrom, Disposable } from "aurelia-framework";
import { Status } from "datatypes/Status";
import { ServiceWorkerHandler } from "libraries/ServiceWorker/ServiceWorkerHandler";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { IServerStatusService, ServerStatus } from "./IServerStatusService";

@autoinject
export class ServerStatusService implements IServerStatusService {
    private static statusCall: string = Api.status;

    private static RefreshInterval: number = 60 * 1000;

    private statusHandlers: ((status: ServerStatus) => Promise<void> | void)[] = [];

    private _status: ServerStatus = ServerStatus.Online; // Assume online unless proven otherwise
    private _statusVerified = false;

    private refreshIntervalHandle: number;

    public currentVersion: string;
    public cachedVersion: string;
    public remoteVersion: string;
    public newVersionAvailable: boolean;

    @computedFrom("_status")
    public get status(): ServerStatus {
        return this._status;
    }

    @computedFrom("_status", "_statusVerified")
    public get online(): Status {
        const state = !this._statusVerified
            ? Status.createInProgress()
            : this._status === ServerStatus.Online
              ? Status.createAvailable()
              : Status.createNotAvailable();
        return state;
    }

    constructor(
        private readonly webRequestHandler: IWebRequestHandler,
        private readonly notificationService: INotificationService,
        private readonly serviceWorkerHandler: ServiceWorkerHandler
    ) {
        this.refreshIntervalHandle = window.setInterval(() => {
            this.updateStatus();
        }, ServerStatusService.RefreshInterval);
        this.updateStatus();
    }

    public addServerStatusHandler(handler: (status: ServerStatus) => Promise<void> | void): Disposable {
        this.statusHandlers.push(handler);
        return {
            dispose: () => {
                const index = this.statusHandlers.findIndex((x) => x === handler);
                if (index === -1) return;

                this.statusHandlers.splice(index, 1);
            },
        };
    }

    public updateStatus(): Promise<ServerStatus> {
        return this.updateServiceAvailability().then(() => {
            const modifiedDate = new Date(document.lastModified);
            this.currentVersion = modifiedDate.toUTCString();

            const request = this.webRequestHandler.createHeadRequest<void>("cache.txt");
            request
                .send()
                .then((x) => {
                    this.remoteVersion = x.headers.get("Last-Modified");

                    if (new Date(this.currentVersion) < new Date(this.remoteVersion)) {
                        this.newVersionAvailable = true;

                        const source = "New version of Portal available";
                        const message = "Refresh your browser to update.";

                        const index = this.notificationService.notifications.findIndex(
                            (x) => x.source == source && x.message == message
                        );

                        if (index < 0) {
                            this.notificationService.registerNotification(source, message, NotificationUrgency.Info);
                        }
                    }
                })
                .catch(() => {
                    this.remoteVersion = "-";
                });

            if (this.serviceWorkerHandler.isSupported) {
                caches.keys().then((x: string[]) => {
                    if (x.length == 1) {
                        this.cachedVersion = x[0].replace("sorama-offline-cache-v", "");

                        if (this.cachedVersion !== this.remoteVersion) this.serviceWorkerHandler.requestUpdateCache();
                    } else {
                        this.cachedVersion = "-";
                    }
                });
            } else {
                this.cachedVersion = "-";
            }

            return this.status;
        });
    }

    private updateServiceAvailability(): Promise<void> {
        return this.isServerOnline()
            .then(() => {
                return ServerStatus.Online;
            })
            .catch(() => {
                return window.navigator.onLine ? ServerStatus.Unreachable : ServerStatus.Offline;
            })
            .then(this.notifyHandlers)
            .then((newState) => {
                this._status = newState;
                this._statusVerified = true;
            });
    }

    private isServerOnline(): Promise<void> {
        // Attempt to connect to the sorama server
        return this.webRequestHandler
            .createGetRequest<void>(ServerStatusService.statusCall)
            .send()
            .then(() => {});
    }

    private notifyHandlers = (newState: ServerStatus): Promise<ServerStatus> => {
        return Promise.all(this.statusHandlers.map((x) => x(newState)))
            .catch(() => {})
            .then(() => newState);
    };
}
